import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Alert, Col, Form, Row } from 'react-bootstrap'
import PageHeader, { PageHeaderButton } from '../../../layout/page/PageHeader'
import MainPanel from './panels/MainPanel'
import ControlsPanel from './panels/ControlsPanel'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import * as Yup from 'yup'
import { useAppSelector } from '../../../../store/hooks'
import { useNavigate } from 'react-router-dom'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import { useToast } from '../../../common/toast/hooks/useToast'
import { Language } from '../../../../features/security/security.slice'
import {
    getGroupTranslation,
    getLocalStorageValue,
} from '../../../common/utility/helpers'
import { BuilderPage } from '../pages/Page.d'
import { constants } from './utils/constants'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import Common from '../../../common/Common'
import { Formik, Form as FormikForm } from 'formik'
import { useReactToPrint } from 'react-to-print'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { PreviewTheme } from './PageBuilder.d'

const PageBuilderForm = ({ id }) => {
    const isViewOrEdit = id !== null
    const pageCode = isViewOrEdit ? 'PAGE_' + id : 'TMPSET'
    const [selectedPage, setSelectedPage] = useState<string>(id)

    const [items, setItems] = useState<any>([
        {
            id: '777',
            type: constants.toolsTypes.HEADER,
            pagePart: {},
            w: 12,
            h: constants.toolsTypes.HEADER.height,
            isDroppable: false,
            isDraggable: false,
            isResizable: false,
            isBounded: false,
        },
    ])

    const [isEditing, setIsEditing] = useState<boolean>(false)
    const [toolboxItem, setToolboxItem] = useState<any>(null)

    const initData = useAppSelector((state: any) => state.security.initData)
    const theme = useAppSelector((state) => state.theme.theme)

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptyPage: BuilderPage = {
        page_title: '',
        code: '',
        name: '',
        header_size_id: null,
        meta_description: '',
        meta_keywords: '',
        main_header_top: '',
        main_header_middle_big: '',
        main_header_bottom: '',
        header_image_id: null,
        header_image: '',
        remove_header_image: false,
        is_template: false,
        ctas: {},
        pillars: [],
    }

    const settings = getLocalStorageValue('settings', {})

    const [page, setPage] = useState<BuilderPage>(emptyPage)
    const [availablePages, setAvailablePages] = useState<[]>([])
    const [headerSizes, setHeaderSizes] = useState<[]>([])
    const [headerSizeCodes, setHeaderSizeCodes] = useState<{}>({})
    const [deletedCtas, setDeletedCtas] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [allowRemoveImage, setAllowRemoveImage] = useState(false)
    const [previewSrc, setPreviewSrc] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})
    const [originalPreviewTheme, setOriginalPreviewTheme] =
        useState<PreviewTheme | null>(null)
    const [previewTheme, setPreviewTheme] = useState<PreviewTheme | null>(null)

    let schema = {
        code: Yup.string().trim().required('Enter Page Url'),
        name: Yup.string().trim().required('Enter Page Internal Name'),
    }

    let _initialValues = {
        ...page,
        value: pageCode + '_VALUE',
        main_header_top: pageCode + '_MAIN_HEADER_TOP',
        main_header_middle_big: pageCode + '_MAIN_HEADER_MIDDLE_BIG',
        main_header_bottom: pageCode + '_MAIN_HEADER_BOTTOM',
    }

    const [initialValues, setInitialValues] = useState(_initialValues)

    const PageFormSchema = Yup.object().shape(schema)

    const componentRef = useRef()
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    })

    const [buttons] = useState<PageHeaderButton[]>([
        {
            variant: 'warning',
            label: 'Export to Pdf',
            icon: 'fa-solid fa-file-export',
            url: '/content/page-builder',
            onClick: handlePrint,
        },
        {
            variant: 'success',
            label: isSubmitting
                ? 'Saving...'
                : isViewOrEdit
                ? 'Update'
                : 'Create',
            icon: 'fa-solid fa-save',
            onClick: () => {
                const formSubmitGtn = document.getElementById(
                    'page-form-submit-btn'
                )
                formSubmitGtn.click()
            },
        },
    ])

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/page-components/builder/page/' + id
            : '/page-components/builder/page/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                setOriginalPreviewTheme({
                    colors: response.data.data.theme.colors,
                })
                if (response.data.data.page) {
                    setPage(response.data.data.page)
                    setItems(response.data.data.grid_items)

                    setInitialValues((prevState) => {
                        return {
                            ...prevState,
                            ...response.data.data.page,
                        }
                    })

                    Object.keys(initData.languages.data).map((locale) => {
                        setPage((prevState) => {
                            const lang: Language =
                                initData.languages.data[locale]

                            const groupTranslations = getGroupTranslation(
                                response.data.data.translations,
                                [
                                    {
                                        key: 'main_header_top',
                                        code: response.data.data.page_headers
                                            .main_header_top,
                                    },
                                    {
                                        key: 'main_header_middle_big',
                                        code: response.data.data.page_headers
                                            .main_header_middle_big,
                                    },
                                    {
                                        key: 'main_header_bottom',
                                        code: response.data.data.page_headers
                                            .main_header_bottom,
                                    },
                                ],
                                locale
                            )

                            setInitialValues((prevState) => {
                                return {
                                    ...prevState,
                                    [`main_header_top_${lang.id}`]:
                                        groupTranslations['main_header_top'],
                                    [`main_header_middle_big_${lang.id}`]:
                                        groupTranslations[
                                            'main_header_middle_big'
                                        ],
                                    [`main_header_bottom_${lang.id}`]:
                                        groupTranslations['main_header_bottom'],
                                }
                            })

                            return {
                                ...prevState,
                                [`main_header_top_${lang.id}`]:
                                    groupTranslations['main_header_top'],
                                [`main_header_middle_big_${lang.id}`]:
                                    groupTranslations['main_header_middle_big'],
                                [`main_header_bottom_${lang.id}`]:
                                    groupTranslations['main_header_bottom'],
                            }
                        })

                        return true
                    })
                }

                setAvailablePages(response.data.data.available_pages || [])
                setHeaderSizes(response.data.data.header_sizes || [])
                setHeaderSizeCodes(response.data.data.header_size_codes || {})
                setHeaderSizeCodes(response.data.data.header_size_codes || {})

                setPage((prevState) => {
                    return {
                        ...prevState,
                        pillars: response.data.data.pillars || [],
                    }
                })

                if (isViewOrEdit) {
                    setAllowRemoveImage(
                        response.data.data.page.header_image_id !== null
                    )
                    setPreviewSrc(
                        response.data.data.page.header_image_id
                            ? `${process.env.REACT_APP_API_ROOT_URL}/media/download/${response.data.data.page.header_image_id}`
                            : ''
                    )
                } else {
                    setPreviewSrc('')
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load page')
            }
        }
    }, [axiosPrivate, initData.languages.data, id, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        record.remove_header_image =
            !record.header_image &&
            record.header_image_id !== null &&
            !allowRemoveImage

        try {
            const routePath = isViewOrEdit
                ? '/page-components/builder/page/' + id
                : '/page-components/builder/page/create'
            const response = await axiosPrivate
                .post(
                    routePath,
                    { ...record, deletedCtas },
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                )
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: 'Successful',
                    detail: response.data.data.msg || 'Processed successfully',
                })

                if (!isViewOrEdit) {
                    setSelectedPage(response.data.data.page.id)
                } else {
                    loadPage()
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields)
        }
    }

    const handleEditing = (e) => {
        setIsEditing(e.target.checked)
    }

    useEffect(() => {
        if (!selectedPage)
            setPage({
                page_title: '',
                code: '',
                name: '',
                header_size_id: null,
                meta_description: '',
                meta_keywords: '',
                main_header_top: '',
                main_header_middle_big: '',
                main_header_bottom: '',
                header_image_id: null,
                header_image: '',
                remove_header_image: false,
                is_template: false,
                ctas: {},
                pillars: [],
            })

        navigate(
            `/content/page-builder${selectedPage ? '/' + selectedPage : ''}`
        )
    }, [selectedPage, isSubmitting, navigate, isViewOrEdit])

    useEffect(() => {
        if (previewTheme === null)
            setPreviewTheme(
                (settings.previewTheme as PreviewTheme) || originalPreviewTheme
            )
    }, [originalPreviewTheme, previewTheme])

    return (
        <>
            <PageHeader header="Page Builder" buttons={buttons} />
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <div className="d-block d-xl-none">
                <div
                    className={`container-fluid bg-${
                        theme === 'dark' ? 'dark' : 'light'
                    } text-${theme === 'dark' ? 'light' : 'dark'} p-5 shadow`}
                >
                    <div
                        className={`bg-${
                            theme === 'dark' ? 'dark' : 'light'
                        } p-5`}
                    >
                        <h3 className="display-7 text-warning">
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className="me-2"
                            />
                            Page builder is only available for larger screens!
                        </h3>
                    </div>
                </div>
            </div>
            <Formik
                initialValues={initialValues}
                validationSchema={PageFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    return (
                        <FormikForm
                            id="page-form"
                            className="d-none d-xl-block"
                        >
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row
                                className="border-top shadow"
                                style={{
                                    minHeight: '70vh',
                                }}
                            >
                                <Col
                                    md={8}
                                    lg={9}
                                    className="p-3"
                                    ref={componentRef}
                                >
                                    <fieldset style={{ verticalAlign: 'top' }}>
                                        <legend>Sample Preview</legend>
                                        <MainPanel
                                            toolboxItem={toolboxItem}
                                            isEditing={isEditing}
                                            items={items}
                                            formikProps={formikProps}
                                            availablePages={availablePages}
                                            deletedCtas={deletedCtas}
                                            setDeletedCtas={setDeletedCtas}
                                            headerSizeCodes={headerSizeCodes}
                                            previewTheme={previewTheme}
                                        />
                                    </fieldset>
                                </Col>
                                <Col md={4} lg={3} className="p-3 border-start">
                                    <Form.Check
                                        type="switch"
                                        id="custom-switch"
                                        label="Move sections"
                                        className="mb-3"
                                        checked={isEditing}
                                        onChange={handleEditing}
                                    />

                                    <ControlsPanel
                                        selectedPage={selectedPage}
                                        setSelectedPage={setSelectedPage}
                                        page={page}
                                        setPage={setPage}
                                        setToolboxItem={setToolboxItem}
                                        availablePages={availablePages}
                                        formikProps={formikProps}
                                        headerSizes={headerSizes}
                                        allowRemoveImage={allowRemoveImage}
                                        setAllowRemoveImage={
                                            setAllowRemoveImage
                                        }
                                        previewSrc={previewSrc}
                                        setPreviewSrc={setPreviewSrc}
                                        previewTheme={previewTheme}
                                        setPreviewTheme={setPreviewTheme}
                                    />
                                </Col>
                            </Row>

                            <input
                                type="submit"
                                id="page-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )
}

export default PageBuilderForm
